import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const HERO_IMG = "/images/window-quiet.jpg";

const focusAreas = [
  "Depression — persistent or major, mild to severe",
  "Anxiety — generalized, social, panic, OCD and PTSD",
  "Relationship difficulties and recurring patterns",
  "Grief, loss and adjusting to sudden change",
  "Work stress, burnout and empty-nest transitions",
  "Boundaries, self-worth and family dynamics",
];

const conditions = [
  "Academic Concerns",
  "Anxiety",
  "ADHD",
  "Bipolar Disorders",
  "Blended Family Issues",
  "Borderline Personality Disorder",
  "Breakup, Separation & Divorce",
  "Caregiver Stress",
  "Chronic Illness & Medical Issues",
  "Chronic Pain",
  "Communication Problems",
  "Couples",
  "Depression",
  "Emotion Management",
  "Family Conflict",
  "Grief",
  "Infidelity",
  "Life Transitions",
  "Men's Mental Health",
  "Obsessive-Compulsive Disorder (OCD)",
  "Panic",
  "Parenting Issues",
  "Peer Relationships",
  "Perfectionism",
  "Personality Disorders",
  "Phobias",
  "Pregnancy, Prenatal & Postpartum",
  "Relationship Issues",
  "Self-Harm",
  "Sleep Issues",
  "Stress",
  "Suicidal Thoughts",
  "Trauma & PTSD",
  "Women's Mental Health",
  "Work Stress",
];

const Individual = () => {
  usePageMeta(
    "Individual Therapy for Adults in California | Holistic Healing Psychotherapy, Inc.",
    "Individual therapy for adults facing relationship difficulties, anxiety, trauma, life transitions, boundaries and self-worth. California LCSW, secure telehealth."
  );

  return (
    <div data-testid="individual-page">
      <section className="bg-cream-50 pt-14 pb-16 lg:pt-24 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <FadeIn>
              <p className="eyebrow mb-6" data-testid="individual-eyebrow">Individual Therapy</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink" data-testid="individual-headline">
                Individual therapy for life's{" "}
                <em className="text-mauve-500">difficult seasons</em>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-xl" data-testid="individual-subcopy">
                One-to-one work for adults who are carrying more than they let on — and are ready to understand what
                keeps them stuck.
              </p>
              <Link
                to="/contact"
                data-testid="individual-schedule-cta"
                className="inline-flex items-center gap-2 mt-9 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-forest-700"
              >
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
            </FadeIn>
          </div>
          <FadeIn delay={0.15} className="lg:col-span-5 editorial-frame">
            <img
              src={HERO_IMG}
              alt="A person sitting quietly by a tall window in soft daylight"
              className="relative z-10 w-full h-[320px] sm:h-[420px] object-cover"
              data-testid="individual-hero-image"
            />
          </FadeIn>
        </div>
      </section>

      <section className="bg-forest-900 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading dark eyebrow="Areas of Focus" title="What we can work on together." />
          <div className="mt-12 grid sm:grid-cols-2 gap-x-14">
            {focusAreas.map((f, i) => (
              <FadeIn key={f} delay={i * 0.06}>
                <div className="flex gap-5 py-5 border-t border-cream-50/10" data-testid={`individual-focus-${i + 1}`}>
                  <span className="mt-2 w-2.5 h-2.5 rotate-45 bg-gold-500/70 shrink-0" aria-hidden="true" />
                  <p className="text-base text-cream-50/80 leading-relaxed">{f}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <p className="mt-12 text-sm text-cream-50/50 max-w-2xl leading-relaxed" data-testid="individual-note">
              Individual sessions are held virtually — 50 minutes, $220. While individual therapy remains an
              important part of my practice, couples therapy is my primary specialty. If your struggles center on
              your relationship,{" "}
              <Link to="/couples-therapy" className="text-gold-400 hover:text-gold-300 underline underline-offset-4" data-testid="individual-couples-link">
                couples work
              </Link>{" "}
              may be the most direct path forward.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-100 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Conditions Treated" title="Whatever you're carrying, it has a name — and a way forward." />
          <FadeIn delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="conditions-treated-list">
              {conditions.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-forest-800/15 bg-cream-50 px-4 py-2 text-sm text-forest-800 transition-colors duration-300 hover:border-gold-500/60 hover:text-gold-700"
                >
                  {c}
                </span>
              ))}
            </div>
            <p className="mt-8 text-sm text-sage/80 max-w-2xl leading-relaxed" data-testid="conditions-note">
              This list isn't exhaustive — if you don't see your experience named here, reach out anyway. If your
              needs fall outside my scope of practice, I will help you find the right fit.
            </p>
          </FadeIn>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default Individual;
